import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  Download,
  Edit,
  Film,
  Clock,
  HardDrive,
  Play,
  Calendar,
  RefreshCw,
  Info,
  FileText,
  Trash2,
  Users,
  LayoutGrid,
  List,
  FolderPlus,
  Folder,
  FolderOpen,
  MoreVertical,
  Plus,
  X,
  MessageSquare,
} from "lucide-react";
import RecordingModal from "./RecordingModal";
import RenameRecordingModal from "./RenameRecordingModal";
import ParticipantsPopup from "./ParticipantsPopup";
import { toast } from "react-toastify";

import ReactDOM from "react-dom";

import {
  getUserRecordings,
  deleteRecording,
  getRecordingFolders,
  createRecordingFolder,
  updateRecordingFolder,
  deleteRecordingFolder,
  moveRecordingsToFolder,
} from "../../../../services/recordingService";

// Fixed-position context menu rendered via portal to escape sidebar stacking context
const FolderContextMenu = ({ folder, fId, fIdStr, menuRef, onAddSubfolder, onRename, onDelete }) => {
  const innerRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    // Position relative to the parent MoreVertical button
    const parent = innerRef.current?.parentElement;
    if (!parent) return;
    // Find the three-dot button ancestor
    const trigger = document.querySelector(`[data-folder-menu="${fId}"]`);
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    let top = rect.top;
    let left = rect.right + 4;
    // Keep within viewport
    const menuW = 160, menuH = 120;
    if (left + menuW > window.innerWidth - 8) left = rect.left - menuW - 4;
    if (top + menuH > window.innerHeight - 8) top = window.innerHeight - menuH - 8;
    if (top < 8) top = 8;
    setPos({ top, left });
  }, [fId]);

  return ReactDOM.createPortal(
    <div
      ref={(el) => { innerRef.current = el; if (menuRef) menuRef.current = el; }}
      className="fixed z-[9999] bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl py-1 min-w-[140px]"
      style={{ top: pos.top, left: pos.left }}
    >
      <button
        onClick={onAddSubfolder}
        className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2"
      >
        <FolderPlus size={12} /> Add Subfolder
      </button>
      <button
        onClick={onRename}
        className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2"
      >
        <Edit size={12} /> Rename
      </button>
      <button
        onClick={onDelete}
        className="w-full px-3 py-1.5 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
      >
        <Trash2 size={12} /> Delete
      </button>
    </div>,
    document.body
  );
};

// Fixed-position move-to-folder menu rendered via portal to escape card overflow:hidden
const MoveToFolderMenu = ({ recId, menuRef, flatFolders, onMove }) => {
  const innerRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const trigger = document.querySelector(`[data-move-btn="${recId}"]`);
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    let top = rect.bottom + 4;
    let left = rect.right - 160;
    if (left < 8) left = 8;
    if (top + 240 > window.innerHeight - 8) top = rect.top - 240 - 4;
    if (top < 8) top = 8;
    setPos({ top, left });
  }, [recId]);

  return ReactDOM.createPortal(
    <div
      ref={(el) => { innerRef.current = el; if (menuRef) menuRef.current = el; }}
      className="fixed z-[9999] bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl py-1 min-w-[160px] max-h-60 overflow-y-auto"
      style={{ top: pos.top, left: pos.left }}
      onClick={e => e.stopPropagation()}
    >
      <button onClick={() => onMove(recId, null)} className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2">
        <X size={12} /> Remove from folder
      </button>
      {flatFolders.map(f => (
        <button key={f._fId} onClick={() => onMove(recId, f._fId)} className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2" style={{ paddingLeft: 12 + f._depth * 12 }}>
          <Folder size={12} style={{ color: f.color }} /> {f.name}
        </button>
      ))}
    </div>,
    document.body
  );
};

const Recordings = () => {
  const navigate = useNavigate();
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [recordingToRename, setRecordingToRename] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => Number(localStorage.getItem("recordings_perPage")) || 12);
  const [recordingToDelete, setRecordingToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState("");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("recordings_viewMode") || "grid");
  const [searchQuery, setSearchQuery] = useState("");

  // Participants popup
  const [participantsPopupId, setParticipantsPopupId] = useState(null);
  const [participantsPopupData, setParticipantsPopupData] = useState([]);
  const participantsBtnRef = useRef(null);

  // Folder state
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState("all");
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderParentId, setNewFolderParentId] = useState(null);
  const [folderMenuId, setFolderMenuId] = useState(null);
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState("");
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [moveMenuRecordingId, setMoveMenuRecordingId] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});
  const folderMenuRef = useRef(null);
  const moveMenuRef = useRef(null);

  // Close menus on outside click (replaces click-away overlay that blocked menus)
  useEffect(() => {
    if (!folderMenuId && !moveMenuRecordingId) return;
    const handler = (e) => {
      if (folderMenuId && folderMenuRef.current && !folderMenuRef.current.contains(e.target)) {
        setFolderMenuId(null);
      }
      if (moveMenuRecordingId && moveMenuRef.current && !moveMenuRef.current.contains(e.target)) {
        setMoveMenuRecordingId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [folderMenuId, moveMenuRecordingId]);

  const formatDuration = (seconds) => {
    if (!seconds) return "0m";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 MB";
    const gb = bytes / (1024 * 1024 * 1024);
    return gb >= 1 ? `${gb.toFixed(1)} GB` : `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });
  };

  const fetchRecordings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getUserRecordings(1, 9999);
      const recs = data?.recordings || [];
      setRecordings(recs);
    } catch (err) {
      console.error("Error fetching recordings:", err);
      setError("Error fetching recordings");
      setRecordings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFolders = useCallback(async () => {
    try {
      const data = await getRecordingFolders();
      setFolders(data?.folders || []);
    } catch (err) {
      console.error("Error fetching folders:", err);
    }
  }, []);

  useEffect(() => {
    fetchRecordings();
    fetchFolders();
  }, [fetchRecordings, fetchFolders]);

  // Build folder tree from flat list
  const folderTree = useMemo(() => {
    const rootFolders = folders.filter(f => !f.parentId);
    const childMap = {};
    folders.forEach(f => {
      if (f.parentId) {
        const pid = String(f.parentId);
        if (!childMap[pid]) childMap[pid] = [];
        childMap[pid].push(f);
      }
    });
    return { rootFolders, childMap };
  }, [folders]);

  // Get all descendant folder IDs for filtering
  const getDescendantIds = useCallback((folderId) => {
    const ids = [folderId];
    const children = folderTree.childMap[folderId] || [];
    children.forEach(c => {
      const cId = c.id || c._id;
      ids.push(...getDescendantIds(String(cId)));
    });
    return ids;
  }, [folderTree]);

  // Group recordings by sessionGroupId/sessionId
  const groupedRecordings = useMemo(() => {
    const SESSION_WINDOW_MS = 2 * 60 * 60 * 1000; // 2 hours
    // For session-time matching prefer the *actual* recording start time.
    // createdAt is the row insert time, which collapses to within milliseconds
    // for legacy bulk-imported recordings — and we were silently merging
    // unrelated recordings (e.g. one from March 30 and one from March 31)
    // into a single card just because they were imported in the same batch.
    const sessionTime = (r) => new Date(r.recordingStartedAt || r.createdAt).getTime();
    const groups = new Map();
    const ungrouped = [];

    // Step 1: Group by sessionGroupId or sessionId
    for (const rec of recordings) {
      const groupKey = rec.sessionGroupId || rec.sessionId || null;
      if (groupKey) {
        if (!groups.has(groupKey)) groups.set(groupKey, []);
        groups.get(groupKey).push(rec);
      } else {
        ungrouped.push(rec);
      }
    }

    // Step 1b: Merge groups that share a sessionId across members
    const groupKeys = [...groups.keys()];
    for (let i = 0; i < groupKeys.length; i++) {
      const keyA = groupKeys[i];
      if (!groups.has(keyA)) continue;
      const recsA = groups.get(keyA);
      const sessionIdsA = new Set(recsA.map(r => r.sessionId).filter(Boolean));
      for (let j = i + 1; j < groupKeys.length; j++) {
        const keyB = groupKeys[j];
        if (!groups.has(keyB)) continue;
        const recsB = groups.get(keyB);
        // Only merge if they share a sessionId (not by time proximity — that's too aggressive for same-room groups)
        if (recsB.some(r => r.sessionId && sessionIdsA.has(r.sessionId))) {
          recsA.push(...recsB);
          groups.delete(keyB);
          recsB.forEach(r => { if (r.sessionId) sessionIdsA.add(r.sessionId); });
        }
      }
    }

    // Step 2: Try to merge ungrouped (chats/transcripts) into existing groups
    // Match by: same sessionId in any group member, OR same meetingId + time proximity
    const stillStandalone = [];
    for (const rec of ungrouped) {
      const recTime = sessionTime(rec);
      const recSessionId = rec.sessionId;
      let merged = false;
      for (const [, recs] of groups) {
        // Match if any member shares the same sessionId
        if (recSessionId && recs.some(r => r.sessionId === recSessionId)) {
          recs.push(rec);
          merged = true;
          break;
        }
        // Or match by meetingId + time proximity (using real recording time
        // when available, so legacy import batches don't false-merge).
        if (recs[0].meetingId === rec.meetingId) {
          const groupTime = sessionTime(recs[0]);
          if (Math.abs(recTime - groupTime) < SESSION_WINDOW_MS) {
            recs.push(rec);
            merged = true;
            break;
          }
        }
      }
      if (!merged) {
        // Check other standalone items to group by meetingId + time
        const match = stillStandalone.find(s =>
          s.meetingId === rec.meetingId &&
          Math.abs(sessionTime(s) - recTime) < SESSION_WINDOW_MS
        );
        if (match) {
          // Promote both into a new group
          const key = `${rec.meetingId}-${recTime}`;
          if (!groups.has(key)) {
            groups.set(key, [match]);
            stillStandalone.splice(stillStandalone.indexOf(match), 1);
          }
          groups.get(key).push(rec);
        } else {
          stillStandalone.push(rec);
        }
      }
    }

    // Step 3: Build result from groups
    const typePriority = { agora_cloud: 0, puppeteer: 0, transcript_only: 1, chat: 2 };
    const result = [];
    for (const [, recs] of groups) {
      recs.sort((a, b) => {
        const pa = typePriority[a.recordingType] ?? 1;
        const pb = typePriority[b.recordingType] ?? 1;
        if (pa !== pb) return pa - pb;
        return (a.recordingIndex || 0) - (b.recordingIndex || 0);
      });
      const primary = recs[0];
      const totalDur = recs.reduce((sum, r) => sum + (r.duration || 0), 0);
      const allParticipants = [...new Set(recs.flatMap(r => r.participants || []))];
      const chatRec = recs.find(r => r.recordingType === 'chat');
      const transcriptRec = recs.find(r => r.recordingType === 'transcript_only');
      const hasChat = recs.some(r => r.recordingType === 'chat');
      const hasTranscript = recs.some(r => r.recordingType === 'transcript_only');
      const hasVideo = recs.some(r => r.recordingType === 'agora_cloud' || r.recordingType === 'puppeteer');
      result.push({
        ...primary,
        duration: totalDur || primary.duration,
        participants: allParticipants.length > 0 ? allParticipants : primary.participants,
        originalChatUrl: primary.originalChatUrl || chatRec?.originalChatUrl || null,
        _partCount: recs.filter(r => r.recordingType !== 'chat' && r.recordingType !== 'transcript_only').length || 1,
        _groupIds: recs.map(r => r.id || r._id),
        _hasChat: hasChat,
        _hasTranscript: hasTranscript,
        _hasVideo: hasVideo,
        _chatId: chatRec?.id || chatRec?._id || null,
        _transcriptId: transcriptRec?.id || transcriptRec?._id || null,
      });
    }

    for (const rec of stillStandalone) {
      result.push({
        ...rec,
        _partCount: 1,
        _hasChat: rec.recordingType === 'chat',
        _hasTranscript: rec.recordingType === 'transcript_only',
        _hasVideo: rec.recordingType === 'agora_cloud' || rec.recordingType === 'puppeteer',
      });
    }

    // Sort by actual recording time when available so legacy bulk-imported
    // recordings show in chronological order, not in arbitrary import order.
    result.sort((a, b) => sessionTime(b) - sessionTime(a));
    return result;
  }, [recordings]);

  // Filter by folder + search
  const filteredRecordings = useMemo(() => {
    let filtered = groupedRecordings;

    if (selectedFolderId !== "all") {
      const validIds = getDescendantIds(selectedFolderId);
      filtered = filtered.filter(r => r.folderId && validIds.includes(String(r.folderId)));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.meetingId || "").toLowerCase().includes(q) ||
        (r.participants || []).some(p => (typeof p === 'string' ? p : p?.name || '').toLowerCase().includes(q))
      );
    }

    return filtered;
  }, [groupedRecordings, selectedFolderId, searchQuery, getDescendantIds]);

  const totalDuration = filteredRecordings.reduce((sum, r) => sum + (r.duration || 0), 0);
  const totalSize = filteredRecordings.reduce((sum, r) => sum + (r.fileSize || r.compressedSize || 0), 0);

  const paginatedRecordings = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredRecordings.slice(start, start + rowsPerPage);
  }, [filteredRecordings, currentPage, rowsPerPage]);

  const computedTotalPages = Math.ceil(filteredRecordings.length / rowsPerPage) || 1;

  useEffect(() => { setCurrentPage(1); }, [selectedFolderId, searchQuery]);

  const handleRecordingClick = (recording) => navigate(`/dashboard/recordings/${recording.id || recording._id}`);
  const handlePageChange = (p) => { if (p >= 1 && p <= computedTotalPages) setCurrentPage(p); };

  // Participants popup handler
  const openParticipants = (e, popupId, participants) => {
    e.stopPropagation();
    if (participantsPopupId === popupId) {
      setParticipantsPopupId(null);
      return;
    }
    participantsBtnRef.current = e.currentTarget;
    setParticipantsPopupData(participants);
    setParticipantsPopupId(popupId);
  };

  const handleDownload = async (recording) => {
    const recId = recording.id || recording._id;
    if (!recId) return;
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_BASE_URL}/recording/${recId}/download?token=${encodeURIComponent(token)}`;
    const a = document.createElement('a');
    a.href = url;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDelete = async () => {
    if (!recordingToDelete) return;
    setDeleting(true);
    try {
      const idsToDelete = recordingToDelete._groupIds?.length > 0
        ? recordingToDelete._groupIds
        : [recordingToDelete.id || recordingToDelete._id];

      // Track which deletes actually succeeded so we don't optimistically
      // remove rows that the backend rejected (e.g., 403 when the user isn't
      // the owner). Previously the frontend swallowed errors and lied to the
      // user that the recording was deleted, leading to "ghost" rows that
      // reappeared on refresh and were still visible to other users.
      const results = await Promise.allSettled(
        idsToDelete.map((id) => deleteRecording(id))
      );
      const succeededIds = new Set();
      const failedReasons = [];
      results.forEach((r, idx) => {
        if (r.status === 'fulfilled') {
          succeededIds.add(idsToDelete[idx]);
        } else {
          failedReasons.push(r.reason?.response?.data?.message || r.reason?.message || 'Unknown error');
        }
      });

      if (succeededIds.size > 0) {
        setRecordings((prev) => prev.filter((r) => !succeededIds.has(r.id) && !succeededIds.has(r._id)));
      }

      const deletedName = recordingToDelete.name || "Recording";
      setRecordingToDelete(null);
      if (succeededIds.size === idsToDelete.length) {
        setDeleteSuccess(`"${deletedName}" has been deleted.`);
        setTimeout(() => setDeleteSuccess(""), 2000);
      } else if (succeededIds.size === 0) {
        console.error('All recording deletes failed:', failedReasons);
        toast.error(failedReasons[0] || 'Failed to delete recording');
      } else {
        console.warn('Partial recording delete failure:', failedReasons);
        toast.warning(`Deleted ${succeededIds.size} of ${idsToDelete.length} parts; ${failedReasons.length} failed`);
      }
    } catch (err) {
      console.error("Failed to delete recording:", err);
      setRecordingToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  // Folder actions
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await createRecordingFolder(newFolderName.trim(), '#6a1fbf', newFolderParentId);
      setNewFolderName("");
      setShowNewFolderInput(false);
      setNewFolderParentId(null);
      // Auto-expand parent
      if (newFolderParentId) {
        setExpandedFolders(prev => ({ ...prev, [newFolderParentId]: true }));
      }
      fetchFolders();
    } catch (err) {
      console.error("Failed to create folder:", err);
    }
  };

  const handleRenameFolder = async (folderId) => {
    if (!editingFolderName.trim()) return;
    try {
      await updateRecordingFolder(folderId, { name: editingFolderName.trim() });
      setEditingFolderId(null);
      setEditingFolderName("");
      fetchFolders();
    } catch (err) {
      console.error("Failed to rename folder:", err);
    }
  };

  const handleDeleteFolder = async () => {
    if (!folderToDelete) return;
    try {
      await deleteRecordingFolder(folderToDelete.id || folderToDelete._id);
      if (selectedFolderId === (folderToDelete.id || folderToDelete._id)) setSelectedFolderId("all");
      setFolderToDelete(null);
      fetchFolders();
      fetchRecordings();
    } catch (err) {
      console.error("Failed to delete folder:", err);
    }
  };

  const handleMoveToFolder = async (recordingId, folderId) => {
    try {
      const rec = groupedRecordings.find(r => (r.id || r._id) === recordingId);
      const ids = rec?._groupIds?.length > 0 ? rec._groupIds : [recordingId];
      await moveRecordingsToFolder(ids, folderId);
      setMoveMenuRecordingId(null);
      setRecordings(prev => prev.map(r =>
        ids.includes(r.id) || ids.includes(r._id) ? { ...r, folderId: folderId || null } : r
      ));
      fetchFolders();
      const folderName = folderId ? folders.find(f => (f.id || f._id) === folderId)?.name : null;
      setDeleteSuccess(folderId ? `Moved to "${folderName || 'folder'}"` : "Removed from folder");
      setTimeout(() => setDeleteSuccess(""), 2000);
    } catch (err) {
      console.error("Failed to move recording:", err);
      setDeleteSuccess("Failed to move recording");
      setTimeout(() => setDeleteSuccess(""), 2000);
    }
  };

  const toggleFolderExpand = (fId) => {
    setExpandedFolders(prev => ({ ...prev, [fId]: !prev[fId] }));
  };

  // unfiledCount kept for potential future use
  // const unfiledCount = groupedRecordings.filter(r => !r.folderId).length;

  const getVideoUrl = (recording) => recording.s3VideoUrl || recording.fileUrl;
  const hasVideo = (recording) => {
    const rt = recording.recordingType;
    return rt !== 'transcript_only' && rt !== 'chat' && !!getVideoUrl(recording);
  };

  // Render a folder item in sidebar (recursive for subfolders)
  const renderFolderItem = (folder, depth = 0) => {
    const fId = folder.id || folder._id;
    const fIdStr = String(fId);
    const children = folderTree.childMap[fIdStr] || [];
    const hasChildren = children.length > 0;
    const isExpanded = expandedFolders[fIdStr];

    return (
      <div key={fId}>
        {editingFolderId === fId ? (
          <div className="flex items-center gap-1 px-1 py-1" style={{ paddingLeft: depth * 16 + 4 }}>
            <input
              autoFocus
              value={editingFolderName}
              onChange={(e) => setEditingFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRenameFolder(fId);
                if (e.key === "Escape") { setEditingFolderId(null); setEditingFolderName(""); }
              }}
              className="flex-1 px-2 py-1 bg-white/10 border border-white/20 rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-purple-500 min-w-0"
            />
            <button onClick={() => handleRenameFolder(fId)} className="p-1 text-green-400 hover:text-green-300"><Plus size={14} /></button>
            <button onClick={() => { setEditingFolderId(null); setEditingFolderName(""); }} className="p-1 text-gray-400 hover:text-white"><X size={14} /></button>
          </div>
        ) : (
          <div className="relative group">
            <div
              className={`w-full flex items-center gap-1 mb-0.5 transition-colors rounded-lg cursor-pointer ${
                selectedFolderId === fId
                  ? "bg-[#00c853]/15 text-[#00c853] font-medium"
                  : "text-gray-400 hover:bg-white/5 hover:text-white"
              }`}
              style={{ paddingLeft: depth * 16 + 8 }}
            >
              {/* Expand toggle */}
              {hasChildren ? (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleFolderExpand(fIdStr); }}
                  className="p-0.5 rounded hover:bg-white/10 flex-shrink-0"
                >
                  <ChevronDown size={12} className={`transition-transform ${isExpanded ? "" : "-rotate-90"}`} />
                </button>
              ) : (
                <span className="w-4 flex-shrink-0" />
              )}

              <button
                onClick={() => setSelectedFolderId(fId)}
                className="flex-1 flex items-center justify-between gap-1 py-2 pr-1 text-sm min-w-0"
              >
                <span className="flex items-center gap-2 truncate">
                  {selectedFolderId === fId
                    ? <FolderOpen size={15} style={{ color: folder.color }} />
                    : <Folder size={15} style={{ color: folder.color }} />
                  }
                  <span className="truncate">{folder.name}</span>
                </span>
                <span className="flex items-center gap-1 flex-shrink-0">
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${selectedFolderId === fId ? "bg-[#00c853]/20 text-[#00c853]" : "bg-white/10 text-gray-500"}`}>
                    {folder.recordingCount || 0}
                  </span>
                </span>
              </button>

              <span
                data-folder-menu={fId}
                onClick={(e) => { e.stopPropagation(); setFolderMenuId(folderMenuId === fId ? null : fId); }}
                className="p-1 rounded text-gray-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 cursor-pointer"
              >
                <MoreVertical size={12} />
              </span>
            </div>

            {/* Folder context menu - rendered as fixed position to escape sidebar stacking context */}
            {folderMenuId === fId && (
              <FolderContextMenu
                folder={folder}
                fId={fId}
                fIdStr={fIdStr}
                menuRef={folderMenuRef}
                onAddSubfolder={() => {
                  setNewFolderParentId(fId);
                  setShowNewFolderInput(true);
                  setExpandedFolders(prev => ({ ...prev, [fIdStr]: true }));
                  setFolderMenuId(null);
                }}
                onRename={() => { setEditingFolderId(fId); setEditingFolderName(folder.name); setFolderMenuId(null); }}
                onDelete={() => { setFolderToDelete(folder); setFolderMenuId(null); }}
              />
            )}
          </div>
        )}

        {/* Children */}
        {hasChildren && isExpanded && (
          <div>
            {children.map(child => renderFolderItem(child, depth + 1))}
          </div>
        )}

        {/* Inline new subfolder input under this folder */}
        {showNewFolderInput && newFolderParentId === fId && isExpanded && (
          <div className="flex items-center gap-1 py-1" style={{ paddingLeft: (depth + 1) * 16 + 8 }}>
            <input
              autoFocus
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreateFolder();
                if (e.key === "Escape") { setShowNewFolderInput(false); setNewFolderName(""); setNewFolderParentId(null); }
              }}
              placeholder="Subfolder name..."
              className="flex-1 px-2 py-1 bg-white/10 border border-white/20 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500 min-w-0"
            />
            <button onClick={handleCreateFolder} className="p-1 text-green-400 hover:text-green-300"><Plus size={14} /></button>
            <button onClick={() => { setShowNewFolderInput(false); setNewFolderName(""); setNewFolderParentId(null); }} className="p-1 text-gray-400 hover:text-white"><X size={14} /></button>
          </div>
        )}
      </div>
    );
  };

  // Flatten folders for move menu (with indent info)
  const flatFoldersForMove = useMemo(() => {
    const result = [];
    const addFolder = (folder, depth) => {
      const fId = folder.id || folder._id;
      result.push({ ...folder, _depth: depth, _fId: fId });
      const children = folderTree.childMap[String(fId)] || [];
      children.forEach(c => addFolder(c, depth + 1));
    };
    folderTree.rootFolders.forEach(f => addFolder(f, 0));
    return result;
  }, [folderTree]);

  return (
    <div className="min-h-screen text-white p-3 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-white mb-1">Recordings</h1>
          <p className="text-gray-400 text-sm">Manage and organize your recorded sessions</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
          <div className="bg-white/5 rounded-xl p-3 md:p-4 border border-white/10">
            <div className="flex items-center gap-3">
              <Film className="w-5 h-5 text-purple-400" />
              <div>
                <p className="text-xl md:text-2xl font-bold text-white">{filteredRecordings.length}</p>
                <p className="text-xs md:text-sm text-gray-400">Total Recordings</p>
              </div>
            </div>
          </div>
          <div className="bg-white/5 rounded-xl p-3 md:p-4 border border-white/10">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-xl md:text-2xl font-bold text-white">{formatDuration(totalDuration)}</p>
                <p className="text-xs md:text-sm text-gray-400">Total Duration</p>
              </div>
            </div>
          </div>
          <div className="bg-white/5 rounded-xl p-3 md:p-4 border border-white/10">
            <div className="flex items-center gap-3">
              <HardDrive className="w-5 h-5 text-green-400" />
              <div>
                <p className="text-xl md:text-2xl font-bold text-white">{formatFileSize(totalSize)}</p>
                <p className="text-xs md:text-sm text-gray-400">Total Storage</p>
              </div>
            </div>
          </div>
          <div className="bg-white/5 rounded-xl p-3 md:p-4 border border-white/10">
            <div className="flex items-center gap-3">
              <Folder className="w-5 h-5 text-yellow-400" />
              <div>
                <p className="text-xl md:text-2xl font-bold text-white">{folders.length}</p>
                <p className="text-xs md:text-sm text-gray-400">Folders</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search + Controls Row */}
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
            <input
              type="text"
              placeholder="Search recordings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 placeholder-gray-500"
            />
          </div>
          <button
            onClick={() => { setNewFolderParentId(null); setShowNewFolderInput(true); }}
            className="px-4 py-2.5 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 rounded-lg flex items-center justify-center gap-2 transition-all text-sm text-white font-medium"
          >
            <FolderPlus className="w-4 h-4" />
            <span>New Folder</span>
          </button>
          <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg p-0.5">
            <button
              onClick={() => { setViewMode("list"); localStorage.setItem("recordings_viewMode", "list"); }}
              className={`p-2 rounded-md transition-colors ${viewMode === "list" ? "bg-white/15 text-white" : "text-white/40 hover:text-white/70"}`}
              title="List view"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => { setViewMode("grid"); localStorage.setItem("recordings_viewMode", "grid"); }}
              className={`p-2 rounded-md transition-colors ${viewMode === "grid" ? "bg-white/15 text-white" : "text-white/40 hover:text-white/70"}`}
              title="Grid view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={() => { fetchRecordings(); fetchFolders(); }}
            className="px-3 py-2.5 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg flex items-center justify-center transition-colors text-white"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Main content: Sidebar + Recordings */}
        <div className="flex gap-4">
          {/* Folder Sidebar */}
          <div className="w-52 flex-shrink-0 hidden md:block">
            <div className="bg-white/5 rounded-xl border border-white/10 p-3 sticky top-6">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3 px-1">Folders</p>

              {/* All Recordings */}
              <button
                onClick={() => setSelectedFolderId("all")}
                className={`w-full px-3 py-2 rounded-lg text-sm flex items-center justify-between gap-2 mb-0.5 transition-colors ${
                  selectedFolderId === "all"
                    ? "bg-[#00c853]/15 text-[#00c853] font-medium"
                    : "text-gray-400 hover:bg-white/5 hover:text-white"
                }`}
              >
                <span className="flex items-center gap-2 truncate">
                  <Film size={15} />
                  All Recordings
                </span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${selectedFolderId === "all" ? "bg-[#00c853]/20 text-[#00c853]" : "bg-white/10 text-gray-500"}`}>
                  {groupedRecordings.length}
                </span>
              </button>

              {folders.length > 0 && <div className="border-t border-white/10 my-2" />}

              {/* Folder tree */}
              {folderTree.rootFolders.map(folder => renderFolderItem(folder, 0))}

              {/* New root folder input */}
              {showNewFolderInput && !newFolderParentId && (
                <div className="flex items-center gap-1 px-1 py-1 mt-1">
                  <input
                    autoFocus
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreateFolder();
                      if (e.key === "Escape") { setShowNewFolderInput(false); setNewFolderName(""); setNewFolderParentId(null); }
                    }}
                    placeholder="Folder name..."
                    className="flex-1 px-2 py-1 bg-white/10 border border-white/20 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500 min-w-0"
                  />
                  <button onClick={handleCreateFolder} className="p-1 text-green-400 hover:text-green-300"><Plus size={14} /></button>
                  <button onClick={() => { setShowNewFolderInput(false); setNewFolderName(""); setNewFolderParentId(null); }} className="p-1 text-gray-400 hover:text-white"><X size={14} /></button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile folder tabs */}
          <div className="flex md:hidden flex-wrap items-center gap-2 mb-4 w-full">
            <button
              onClick={() => setSelectedFolderId("all")}
              className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 transition-colors ${
                selectedFolderId === "all"
                  ? "bg-[#00c853]/15 text-[#00c853] border border-[#00c853]/30"
                  : "bg-white/5 border border-white/10 text-gray-400"
              }`}
            >
              <Film size={14} /> All <span className="text-xs opacity-60">({groupedRecordings.length})</span>
            </button>
            {folders.map((folder) => (
              <button
                key={folder.id || folder._id}
                onClick={() => setSelectedFolderId(folder.id || folder._id)}
                className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 transition-colors ${
                  selectedFolderId === (folder.id || folder._id)
                    ? "bg-[#00c853]/15 text-[#00c853] border border-[#00c853]/30"
                    : "bg-white/5 border border-white/10 text-gray-400"
                }`}
              >
                <Folder size={14} style={{ color: folder.color }} /> {folder.name}
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className="flex-1 min-w-0">
            {loading && (
              <div className="flex flex-col items-center justify-center h-64 text-center bg-white/5 rounded-xl border border-white/10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400 mb-4"></div>
                <p className="text-gray-400">Loading recordings...</p>
              </div>
            )}

            {error && (
              <div className="flex flex-col items-center justify-center h-64 text-center bg-red-500/10 rounded-xl border border-red-500/20">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {!loading && !error && filteredRecordings.length === 0 && (
              <div className="flex flex-col items-center justify-center h-64 text-center bg-white/5 rounded-xl border border-white/10">
                <Film className="text-gray-600 mb-4" size={48} />
                <h3 className="text-lg font-semibold text-white mb-2">No recordings yet</h3>
                <p className="text-gray-400 text-sm">
                  {searchQuery ? "No recordings match your search" : "Recordings from your studio sessions will appear here"}
                </p>
              </div>
            )}

            {/* Grid View */}
            {!loading && !error && paginatedRecordings.length > 0 && viewMode === "grid" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {paginatedRecordings.map((recording) => {
                  const recId = recording.id || recording._id;
                  return (
                    <div
                      key={recId}
                      className="bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:bg-white/10 hover:border-[#6a1fbf]/30 transition-all cursor-pointer group"
                      onClick={() => handleRecordingClick(recording)}
                    >
                      {/* Thumbnail */}
                      <div className="relative aspect-video bg-gradient-to-br from-[#6a1fbf]/20 to-[#00c853]/10 overflow-hidden">
                        {hasVideo(recording) ? (
                          <video
                            src={getVideoUrl(recording)}
                            preload="metadata"
                            muted
                            className="w-full h-full object-cover"
                            onLoadedData={(e) => { e.target.currentTime = 1; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <FileText className="w-10 h-10 text-purple-400/50" />
                          </div>
                        )}
                        {recording.duration > 0 && (
                          <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-0.5 rounded text-[11px] text-white font-medium">
                            {formatDuration(recording.duration)}
                          </div>
                        )}
                        {hasVideo(recording) && (
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                              <Play className="w-5 h-5 text-white ml-0.5" />
                            </div>
                          </div>
                        )}
                        {/* Content type badges */}
                        <div className="absolute top-2 left-2 flex gap-1">
                          {recording._hasVideo && (
                            <div className="px-1.5 py-0.5 bg-blue-500/80 rounded text-[10px] text-white font-medium">Video</div>
                          )}
                          {recording._hasTranscript && (
                            <div className="px-1.5 py-0.5 bg-[#6a1fbf]/80 rounded text-[10px] text-white font-medium">Notes</div>
                          )}
                          {recording._hasChat && (
                            <div className="px-1.5 py-0.5 bg-emerald-500/80 rounded text-[10px] text-white font-medium">Chat</div>
                          )}
                          {!recording._hasVideo && !recording._hasTranscript && !recording._hasChat && (
                            <div className="px-1.5 py-0.5 bg-[#6a1fbf]/80 rounded text-[10px] text-white font-medium">
                              {recording.recordingType === 'chat' ? 'Chat' : recording.recordingType === 'transcript_only' ? 'Notes' : ''}
                            </div>
                          )}
                        </div>
                        {recording._partCount > 1 && (
                          <div className="absolute top-2 right-2 px-2 py-0.5 bg-[#00c853]/80 rounded text-[10px] text-white font-medium">
                            {recording._partCount} parts
                          </div>
                        )}
                      </div>

                      {/* Card body */}
                      <div className="p-3">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <h3 className="text-sm font-semibold text-white truncate flex-1 min-w-0">{recording.name || "Untitled Recording"}</h3>
                          <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100">
                            <button
                              data-move-btn={recId}
                              onClick={(e) => { e.stopPropagation(); setMoveMenuRecordingId(moveMenuRecordingId === recId ? null : recId); }}
                              className="p-1 rounded-md text-white/30 hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
                              title="Move to folder"
                            >
                              <Folder size={14} />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setRecordingToRename(recording); setShowRenameModal(true); }}
                              className="p-1 rounded-md text-white/30 hover:text-purple-400 hover:bg-purple-500/10 transition-colors"
                              title="Rename"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setRecordingToDelete(recording); }}
                              className="p-1 rounded-md text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {formatDate(recording.recordingStartedAt || recording.createdAt)}
                          </span>
                          {recording.participants?.length > 0 && (
                            <button
                              onClick={(e) => openParticipants(e, `grid-${recId}`, recording.participants)}
                              className="flex items-center gap-1 hover:text-white transition-colors"
                            >
                              <Users size={12} />
                              {recording.participants.length}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* List View */}
            {!loading && !error && paginatedRecordings.length > 0 && viewMode === "list" && (
              <div className="space-y-3">
                {paginatedRecordings.map((recording) => {
                  const recId = recording.id || recording._id;
                  return (
                    <div
                      key={recId}
                      className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-5 hover:bg-white/10 transition-all cursor-pointer"
                      onClick={() => handleRecordingClick(recording)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-base md:text-lg font-semibold text-white truncate">{recording.name || "Untitled Recording"}</h3>
                            {(() => {
                              // Reassurance badge: surface in-flight recordings in the list so
                              // users don't assume a capture failed just because the video
                              // thumbnail/duration hasn't materialized yet. Mirrors the
                              // pipelineState computation on the detail page.
                              const isLive = recording.status === 'recording'
                                || recording.layoutProcessingStatus === 'record-pending'
                                || recording.layoutProcessingStatus === 'recording'
                                || recording.layoutProcessingStatus === 'record-paused';
                              // compressionStatus defaults to 'pending' on every Recording
                              // doc even when compression is never run (worker-recorder
                              // mode skips the compression pipeline entirely). Treat
                              // only the *active* compression states as "Processing"
                              // — 'pending' just means "compression hasn't started",
                              // which for worker-recorder is the terminal state.
                              const isCompressing = ['downloading', 'compressing', 'uploading'].includes(recording.compressionStatus);
                              const isFinalizing = recording.layoutProcessingStatus === 'record-stopping'
                                || recording.layoutProcessingStatus === 'raw'
                                || recording.layoutProcessingStatus === 'processing'
                                || isCompressing;
                              if (isLive) {
                                return (
                                  <span className="px-2 py-0.5 bg-red-500/20 border border-red-500/30 rounded-full text-[10px] text-red-300 whitespace-nowrap flex items-center gap-1.5">
                                    <span className="relative flex h-1.5 w-1.5">
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
                                    </span>
                                    Recording
                                  </span>
                                );
                              }
                              if (isFinalizing) {
                                return (
                                  <span className="px-2 py-0.5 bg-amber-500/20 border border-amber-500/30 rounded-full text-[10px] text-amber-300 whitespace-nowrap flex items-center gap-1">
                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                    Processing
                                  </span>
                                );
                              }
                              return null;
                            })()}
                            {recording._partCount > 1 && (
                              <span className="px-2 py-0.5 bg-[#00c853]/20 border border-[#00c853]/30 rounded-full text-[10px] text-green-300 whitespace-nowrap flex items-center gap-1">
                                <Film className="w-3 h-3" />{recording._partCount} parts
                              </span>
                            )}
                            {recording._hasVideo && (
                              <span className="px-2 py-0.5 bg-blue-500/20 border border-blue-500/30 rounded-full text-[10px] text-blue-300 whitespace-nowrap flex items-center gap-1">
                                <Film className="w-3 h-3" />Video
                              </span>
                            )}
                            {recording._hasTranscript && (
                              <span className="px-2 py-0.5 bg-[#6a1fbf]/20 border border-[#6a1fbf]/30 rounded-full text-[10px] text-purple-300 whitespace-nowrap flex items-center gap-1">
                                <FileText className="w-3 h-3" />Notes
                              </span>
                            )}
                            {recording._hasChat && (
                              <span className="px-2 py-0.5 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-[10px] text-emerald-300 whitespace-nowrap flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" />Chat
                              </span>
                            )}
                            {!recording._hasVideo && !recording._hasTranscript && !recording._hasChat && (recording.recordingType === 'transcript_only' || recording.recordingType === 'chat') && (
                              <span className="px-2 py-0.5 bg-[#6a1fbf]/20 border border-[#6a1fbf]/30 rounded-full text-[10px] text-purple-300 whitespace-nowrap flex items-center gap-1">
                                <FileText className="w-3 h-3" />{recording.recordingType === 'chat' ? 'Chat Only' : 'Notes Only'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-3 md:gap-4 text-sm text-gray-400 mb-4">
                        <div className="flex items-center gap-1.5"><Calendar size={14} /><span>{formatDate(recording.recordingStartedAt || recording.createdAt)}</span></div>
                        {recording.duration > 0 && <div className="flex items-center gap-1.5"><Clock size={14} /><span>{formatDuration(recording.duration)}</span></div>}
                        {recording.participants?.length > 0 && (
                          <button
                            onClick={(e) => openParticipants(e, recId, recording.participants)}
                            className="flex items-center gap-1.5 hover:text-white transition-colors"
                          >
                            <Users size={14} /><span>{recording.participants.length} participant{recording.participants.length !== 1 ? 's' : ''}</span>
                          </button>
                        )}
                      </div>

                      {/* Video platform upload badges */}
                      {(recording.videoUpload?.youtube?.status || recording.videoUpload?.vimeo?.status) && (
                        <div className="flex items-center gap-2 mb-3">
                          {recording.videoUpload?.youtube?.status && (
                            recording.videoUpload.youtube.status === 'completed' ? (
                              <a href={recording.videoUpload.youtube.videoUrl} target="_blank" rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1.5 px-2 py-1 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400 hover:bg-red-500/20 transition-colors">
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                                YouTube
                              </a>
                            ) : recording.videoUpload.youtube.status === 'uploading' || recording.videoUpload.youtube.status === 'pending' ? (
                              <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs text-yellow-400">
                                <RefreshCw className="w-3 h-3 animate-spin" /> YouTube uploading...
                              </span>
                            ) : recording.videoUpload.youtube.status === 'failed' ? (
                              <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
                                YouTube upload failed
                              </span>
                            ) : null
                          )}
                          {recording.videoUpload?.vimeo?.status && (
                            recording.videoUpload.vimeo.status === 'completed' ? (
                              <a href={recording.videoUpload.vimeo.videoUrl} target="_blank" rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-500/10 border border-blue-500/20 rounded text-xs text-blue-400 hover:bg-blue-500/20 transition-colors">
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881L5.322 11.4C4.603 8.816 3.834 7.522 3.01 7.522c-.179 0-.806.378-1.881 1.132L0 7.197a315.065 315.065 0 0 0 3.501-3.123C5.08 2.701 6.266 1.984 7.055 1.91c1.867-.18 3.016 1.1 3.447 3.838.465 2.953.789 4.789.971 5.507.539 2.45 1.131 3.674 1.776 3.674.502 0 1.256-.796 2.265-2.385 1.004-1.589 1.54-2.797 1.612-3.628.144-1.371-.395-2.061-1.614-2.061-.574 0-1.167.121-1.777.391 1.186-3.868 3.434-5.757 6.762-5.637 2.473.06 3.628 1.664 3.493 4.797l-.013.01z"/></svg>
                                Vimeo
                              </a>
                            ) : recording.videoUpload.vimeo.status === 'uploading' || recording.videoUpload.vimeo.status === 'pending' ? (
                              <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs text-yellow-400">
                                <RefreshCw className="w-3 h-3 animate-spin" /> Vimeo uploading...
                              </span>
                            ) : recording.videoUpload.vimeo.status === 'failed' ? (
                              <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
                                Vimeo upload failed
                              </span>
                            ) : null
                          )}
                        </div>
                      )}

                      <div className="flex items-center gap-2 flex-wrap">
                        {(recording.recordingType === 'transcript_only' || recording.recordingType === 'chat') ? (
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/recordings/${recId}`); }} className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 rounded-lg flex items-center gap-1.5 text-sm text-white transition-colors">
                            <FileText className="w-4 h-4" />{recording.recordingType === 'chat' ? 'View Chat' : 'View Notes'}
                          </button>
                        ) : (
                          <>
                            <button onClick={(e) => { e.stopPropagation(); handleRecordingClick(recording); }} className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 rounded-lg flex items-center gap-1.5 text-sm text-white transition-colors"><Play className="w-4 h-4" />Play</button>
                            <button onClick={(e) => { e.stopPropagation(); handleDownload(recording); }} className="px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg flex items-center gap-1.5 text-sm text-white transition-colors"><Download className="w-4 h-4" />Download</button>
                          </>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/recordings/${recId}`); }} className="px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg flex items-center gap-1.5 text-sm text-white transition-colors"><Info className="w-4 h-4" />Details</button>
                        <button onClick={(e) => { e.stopPropagation(); setRecordingToRename(recording); setShowRenameModal(true); }} className="px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg flex items-center gap-1.5 text-sm text-white transition-colors"><Edit className="w-4 h-4" />Rename</button>

                        {/* Move to folder */}
                        <button
                          data-move-btn={recId}
                          onClick={(e) => { e.stopPropagation(); setMoveMenuRecordingId(moveMenuRecordingId === recId ? null : recId); }}
                          className="px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg flex items-center gap-1.5 text-sm text-white transition-colors"
                        >
                          <Folder className="w-4 h-4" />Move
                        </button>

                        <button onClick={(e) => { e.stopPropagation(); setRecordingToDelete(recording); }} className="px-3 py-1.5 bg-white/10 hover:bg-red-500/20 border border-white/10 hover:border-red-500/30 rounded-lg flex items-center gap-1.5 text-sm text-white hover:text-red-400 transition-colors ml-auto"><Trash2 className="w-4 h-4" />Delete</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {!loading && !error && filteredRecordings.length > 0 && computedTotalPages > 1 && (
              <div className="bg-white/5 rounded-xl border border-white/10 px-4 md:px-6 py-3 md:py-4 mt-4">
                <div className="flex flex-col md:flex-row items-center justify-between gap-3 md:gap-0">
                  <div className="flex items-center space-x-2 text-xs md:text-sm text-white">
                    <span>Per page:</span>
                    <select
                      value={rowsPerPage}
                      onChange={(e) => { const v = Number(e.target.value); localStorage.setItem("recordings_perPage", v); setRowsPerPage(v); setCurrentPage(1); }}
                      className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-white text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 hover:bg-white/10 transition-colors"
                    >
                      {[12, 24, 48, 96].map(n => <option key={n} value={n} className="bg-gray-800 text-white">{n}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center space-x-2 md:space-x-4">
                    <span className="text-xs md:text-sm text-white">{currentPage} of {computedTotalPages}</span>
                    <div className="flex items-center space-x-1">
                      <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="p-1.5 md:p-1 hover:bg-white/10 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><ChevronsLeft className="h-4 w-4 text-white" /></button>
                      <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 md:p-1 hover:bg-white/10 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><ChevronLeft className="h-4 w-4 text-white" /></button>
                      <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === computedTotalPages} className="p-1.5 md:p-1 hover:bg-white/10 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><ChevronRight className="h-4 w-4 text-white" /></button>
                      <button onClick={() => handlePageChange(computedTotalPages)} disabled={currentPage === computedTotalPages} className="p-1.5 md:p-1 hover:bg-white/10 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><ChevronsRight className="h-4 w-4 text-white" /></button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-400 text-xs md:text-sm space-y-1 px-2 pt-6">
          <p>R-Link Email Support:</p>
          <a href="mailto:support@r-link.com" className="underline cursor-pointer hover:text-white transition-colors my-4">support@r-link.com</a>
          <p>Copyright &copy; R-Link LLC 2025.</p>
        </div>
      </div>

      {/* Move to folder menu (portal) */}
      {moveMenuRecordingId && (
        <MoveToFolderMenu
          recId={moveMenuRecordingId}
          menuRef={moveMenuRef}
          flatFolders={flatFoldersForMove}
          onMove={handleMoveToFolder}
        />
      )}

      {/* Participants Popup (fixed position, rendered once) */}
      {participantsPopupId && (
        <ParticipantsPopup
          participants={participantsPopupData}
          onClose={() => setParticipantsPopupId(null)}
          anchorRef={participantsBtnRef}
        />
      )}

      {/* Recording Modal */}
      <RecordingModal isOpen={showModal} onClose={() => { setShowModal(false); setSelectedRecording(null); }} recording={selectedRecording} />

      {/* Rename Recording Modal */}
      <RenameRecordingModal
        isOpen={showRenameModal}
        onClose={() => { setShowRenameModal(false); setRecordingToRename(null); }}
        recording={recordingToRename}
        onSave={(renamedRecordingId, newName) => {
          setRecordings((prev) => prev.map((rec) => (rec.id || rec._id) === renamedRecordingId ? { ...rec, name: newName } : rec));
          setRecordingToRename(null);
          setShowRenameModal(false);
        }}
      />

      {/* Delete Recording Confirmation */}
      {recordingToDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#1a1a2e] border border-white/10 rounded-xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="text-lg font-semibold text-white mb-2">Delete Recording</h3>
            <p className="text-sm text-gray-400 mb-1">Are you sure you want to delete this recording?</p>
            <p className="text-sm text-white font-medium mb-4 truncate">{recordingToDelete.name || "Untitled Recording"}</p>
            <p className="text-xs text-red-400/80 mb-5">This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setRecordingToDelete(null)} disabled={deleting} className="px-4 py-2 text-sm text-white bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg transition-colors">Cancel</button>
              <button onClick={handleDelete} disabled={deleting} className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50">{deleting ? "Deleting..." : "Delete"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Folder Confirmation */}
      {folderToDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#1a1a2e] border border-white/10 rounded-xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="text-lg font-semibold text-white mb-2">Delete Folder</h3>
            <p className="text-sm text-gray-400 mb-1">Delete folder "{folderToDelete.name}"?</p>
            <p className="text-xs text-gray-500 mb-5">Recordings in this folder will not be deleted.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setFolderToDelete(null)} className="px-4 py-2 text-sm text-white bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg transition-colors">Cancel</button>
              <button onClick={handleDeleteFolder} className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {deleteSuccess && (
        <div className="fixed bottom-6 right-6 z-50 bg-green-600 text-white px-5 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          <span className="text-sm font-medium">{deleteSuccess}</span>
        </div>
      )}

    </div>
  );
};

// New folder-based view is now live for all users
export default Recordings;
